import { aiDraftsEn } from './ai-drafts/en';
import { automationsEn } from './automations/en';
import { blockingEn } from './blocking/en';
import { cachingEn } from './caching';
import { channelsEn } from './channels/en';
import { chatWidgetEn } from './chat-widget/en';
import { checksEn } from './checks';
import { commerceEn } from './commerce/en';
import { decisionsEn } from './decisions';
import { developerEn } from './developer/en';
import { offersEn } from './offers/en';
import { privacyEn } from './privacy/en';
import { reportsEn } from './reports/en';
import { slaEn } from './sla/en';
import { ticketsEn } from './tickets/en';
import { toolsEn } from './tools';
import { chatSettingsEn } from './widget/en';

/** Studio interface text in English (the source language). Keep sentences short and plain. */
export const en = {
  app: {
    name: 'Hamnivo Studio',
    skipToContent: 'Skip to content',
    language: 'Language',
    translationNote: '',
    close: 'Close',
    cancel: 'Cancel',
    retry: 'Try again',
    reference: 'Reference',
    copied: 'Copied',
    optional: 'Optional',
    serverDetail: 'From the server: {{detail}}',
    cancelled: 'Cancelled. Nothing was changed.',
    loading: 'Loading',
  },
  nav: {
    label: 'Studio',
    home: 'Home',
    inbox: 'Inbox',
    ai: 'AI assistant',
    sales: 'Sales',
    settings: 'Settings',
    sections: '{{name}} pages',
    brands: 'Brands and install',
    agents: 'AI agents',
    models: 'Model connections',
    knowledge: 'Knowledge',
    memory: 'Customer memory',
    runs: 'Activity',
    team: 'Team and access',
    security: 'Security',
    platform: 'Platform console',
    account: 'Account security',
    openMenu: 'Open menu',
    menu: 'Menu',
    switchWorkspace: 'Switch workspace',
    workspace: 'Workspace',
    signOut: 'Sign out',
    signedInAs: 'Signed in as',
  },
  errors: {
    network: {
      title: 'We couldn’t reach Hamnivo',
      description: 'Check your connection and try again.',
    },
    denied: {
      title: 'You don’t have permission to do this',
      description: 'Ask a workspace owner or administrator for access.',
    },
    notFound: {
      title: 'We couldn’t find this',
      description: 'It may have been removed, or the link is wrong.',
    },
    validation: {
      title: 'Some information needs attention',
      description: 'Check the details and try again.',
    },
    rateLimited: {
      title: 'Too many requests',
      description: 'Wait a moment, then try again.',
      retryIn: 'You can try again in {{seconds}} s.',
      retryNow: 'You can try again now.',
    },
    unavailable: {
      title: 'A connected service isn’t responding',
      description: 'Nothing was changed. Try again in a moment.',
    },
    idempotency: {
      title: 'This was already sent with different details',
      description: 'Reload the page to see what was saved.',
    },
    conflict: {
      title: 'Someone else changed this first',
      description: 'Reload to see the latest version.',
    },
    mfa: {
      title: 'Two-factor authentication needed',
      description: 'Turn on two-factor authentication in Account security, then try again.',
    },
    tooLarge: { title: 'File too large', description: 'Files can be up to 5 MB.' },
    rejected: { title: 'File rejected', description: 'The file could not be accepted.' },
    disabled: {
      title: 'This feature is turned off',
      description: 'Ask your administrator to turn it on.',
    },
    internal: {
      title: 'Something went wrong on our side',
      description: 'Nothing was changed. Try again.',
    },
    planLimit: {
      title: 'The plan limit is reached',
      description:
        'Your plan doesn’t include more of this. Free some up, or ask the workspace owner about a larger plan.',
    },
    reauth: {
      title: 'Sign in again to continue',
      description: 'For your safety, this needs a recent sign-in.',
      action: 'Sign in again',
    },
    channelWindow: {
      title: 'Only template messages can be sent now',
      description:
        'WhatsApp allows replies for 24 hours after the customer last wrote. Nothing was sent. You can reply again after the customer writes.',
    },
    unknown: { title: 'Something went wrong', description: 'Try again.' },
    page: {
      title: 'This page couldn’t load',
      description: 'Try again. If it keeps happening, reload the page.',
    },
  },
  states: {
    stale: {
      since: 'Couldn’t refresh this. Showing what was loaded {{time}}.',
      unknown: 'Couldn’t refresh this. What you see may be out of date.',
    },
    forbiddenTitle: 'You don’t have access to this page',
    forbiddenDescription: 'Ask a workspace owner or administrator to give you access.',
    backToOverview: 'Back to overview',
    notFoundTitle: 'Page not found',
    notFoundDescription: 'The address may be wrong, or the page was moved.',
  },
  auth: {
    email: 'Email address',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    errorSummary: 'Fix these to continue',
    emailRequired: 'Enter your email address.',
    emailInvalid: 'Enter an email address such as name@example.com.',
    passwordRequired: 'Enter your password.',
    rateLimited: 'Too many attempts. Wait a few minutes, then try again.',
    privacy:
      'Studio keeps you signed in with a secure cookie. What you type stays in this tab until you save it.',
    signIn: {
      title: 'Welcome back',
      description: 'Sign in to Hamnivo Studio to continue.',
      keepSignedIn: 'Keep me signed in on this device',
      keepSignedInHint:
        'Leave this off on a shared computer. You are then signed out when the browser closes.',
      submit: 'Sign in',
      or: 'or',
      passkey: 'Sign in with a passkey',
      forgot: 'Forgot your password?',
      noAccount: 'New to Hamnivo?',
      createAccount: 'Create an account',
      failed: 'We couldn’t sign you in. Check your email and password, or reset your password.',
      notVerified: 'Confirm your email address first. We sent you a link when you signed up.',
      resendVerification: 'Send the link again',
      verificationSent: 'We sent a new link. Check your email.',
      passkeyFailed: 'The passkey didn’t work. Try again, or sign in with your password.',
    },
    twoFactor: {
      title: 'Verify it’s you',
      description: 'Enter the 6-digit code from your authenticator app.',
      code: '6-digit code',
      codeHint: 'The code changes every 30 seconds.',
      submit: 'Verify',
      useBackup: 'Use a backup code instead',
      useApp: 'Use a code from your app instead',
      backupCode: 'Backup code',
      backupHint: 'One of the codes you saved when you turned on two-factor authentication.',
      codeRequired: 'Enter the code.',
      invalid: 'That code didn’t work. Check it and try again.',
      expired: 'This sign-in attempt expired. Sign in again to get a new challenge.',
      tooMany: 'Too many attempts. Sign in again to continue.',
      back: 'Back to sign in',
    },
    signUp: {
      title: 'Create your account',
      description: 'Next, you can create a workspace or join one you were invited to.',
      name: 'Your name',
      nameRequired: 'Enter your name.',
      passwordHint: 'At least 12 characters. A short sentence is easy to remember.',
      passwordShort: 'Use at least 12 characters.',
      passwordLong: 'Use at most 256 characters.',
      submit: 'Create account',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      failed:
        'We couldn’t create this account. If you already have one, sign in or reset your password.',
    },
    checkEmail: {
      title: 'Check your email',
      verify: 'We sent a link to {{email}}. Open it to confirm your address and finish signing up.',
      reset:
        'If an account uses {{email}}, we sent it a link to choose a new password. The link works for one hour.',
      help: 'Can’t find it? Check your spam folder, or wait a minute and send it again.',
      resend: 'Send the link again',
      sent: 'We sent a new link.',
      back: 'Back to sign in',
    },
    forgot: {
      title: 'Reset your password',
      description:
        'Enter your account’s email address. We’ll send a link to choose a new password.',
      submit: 'Send reset link',
    },
    reset: {
      title: 'Choose a new password',
      description: 'Your other sessions are signed out when you change it.',
      password: 'New password',
      submit: 'Save new password',
      invalidLink: 'This reset link is invalid or has expired. Ask for a new one.',
      requestNew: 'Ask for a new link',
      done: 'Your password was changed. Sign in with the new password.',
    },
    reauth: {
      stepUpTitle: 'Confirm it’s you',
      stepUpDescription:
        'This action needs a recent sign-in. Enter your password to continue. Your work on this page is kept.',
      expiredTitle: 'Sign in to continue',
      expiredDescription:
        'Your session ended. Sign in again to continue. Unsaved changes on this page are kept until you do.',
      account: 'Account',
      confirm: 'Confirm',
      signInAgain: 'Sign in again',
      otherAccount: 'Sign in as someone else',
      masked: 'Workspace content is hidden until you sign in.',
    },
  },
  tenants: {
    title: 'Choose a workspace',
    description:
      'Workspaces keep brands, people and conversations apart. Studio remembers your choice in this browser.',
    yours: 'Your workspaces',
    open: 'Open',
    current: 'Current',
    unavailable: 'You can’t open this workspace right now.',
    status: {
      active: 'Active',
      suspended: 'Suspended',
      closing: 'Closing',
      invited: 'Invitation pending',
      removed: 'Removed',
    },
    empty: {
      title: 'No workspaces yet',
      description: 'Create one to start, or ask a teammate to invite you.',
    },
    create: {
      title: 'Create a workspace',
      name: 'Workspace name',
      nameHint: 'Usually your company or team name.',
      slug: 'Workspace address',
      slugHint: '3 to 40 lowercase letters, digits or hyphens. Used in links.',
      language: 'Default language',
      submit: 'Create workspace',
      nameRequired: 'Enter a workspace name.',
      slugInvalid:
        'Use 3 to 40 lowercase letters, digits or hyphens, starting and ending with a letter or digit.',
      slugTaken: 'That address is taken. Try another one.',
      created: 'Workspace created.',
    },
    revoked: {
      title: 'Your access to this workspace changed',
      description: 'You can’t open {{name}} any more. Its data was cleared from this tab.',
    },
  },
  shell: {
    aiStopped: 'AI stopped',
    stopAi: 'Stop AI',
    stopAiLabel: 'Stop AI replies',
    killBanner: {
      title: 'AI replies are stopped',
      people: 'People keep chatting. New messages go to your team.',
      review: 'Review stops',
    },
    scope: {
      tenant: 'Everywhere in this workspace',
      brand: 'Brand: {{name}}',
      agent: 'Agent: {{name}}',
    },
    reason: 'Reason: {{reason}}',
    since: 'Since {{time}}',
  },
  overview: {
    degraded: {
      title: 'Part of Home couldn’t load',
      description:
        'The rest of Studio works, including your inbox. Each part below can be tried again on its own.',
    },
    title: 'Overview',
    greeting: 'Welcome back, {{name}}.',
    chats: {
      title: 'Your chats',
      description: 'Customers waiting for a person, and chats assigned to you.',
      loading: 'Loading your chats',
      waiting: 'Waiting for a person',
      mine: 'Assigned to you',
      caughtUp: 'Nothing is waiting for you right now.',
      open: 'Open inbox',
    },
    setup: {
      title: 'Get set up',
      description: 'These steps are left before customers can chat with your AI assistant.',
      steps: {
        engine: {
          title: 'Connect the chat engine',
          description: 'Connect a brand so that its chats reach your team.',
        },
        brand: {
          title: 'Add a brand and install the chat',
          description: 'Name it, allow your website and paste the install code there.',
        },
        agent: {
          title: 'Publish an AI agent',
          description: 'Your assistant answers customers only after you publish it.',
        },
        team: {
          title: 'Invite a teammate',
          description: 'Add the people who will answer chats with you.',
        },
      },
    },
    brands: {
      title: 'Brands',
      description: 'Chat connection and AI status for each brand.',
      loading: 'Loading brands',
      columns: { chat: 'Chat connection', ai: 'AI assistant' },
      install: 'Install',
      openAgent: 'Open agent',
      empty: {
        title: 'No brands yet',
        description: 'A brand has its own chat widget, allowed websites and AI assistant.',
        action: 'Create a brand',
      },
    },
    stops: {
      title: 'AI stop switches',
      none: 'The AI isn’t stopped anywhere.',
      loading: 'Loading stop switches',
      manage: 'Manage stops',
    },
    runs: {
      title: 'Latest runs',
      description: 'What the AI did most recently. No message text is kept here.',
      loading: 'Loading runs',
      viewAll: 'View all runs',
      empty: {
        title: 'No runs yet',
        description: 'Runs appear when the AI answers or drafts in a conversation.',
      },
    },
  },
  status: {
    connection: {
      active: 'Connected',
      provisioning: 'Setting up',
      degraded: 'Needs attention',
      disabled: 'Disabled',
      none: 'Not connected',
    },
    agent: {
      live: 'Live · v{{version}}',
      draftOnly: 'Draft only',
      draftAhead: 'Draft v{{version}} not live',
      archived: 'Archived',
      none: 'No agent',
    },
  },
  brands: {
    title: 'Brands and install',
    description: 'Create a brand, connect it to Chatwoot and add the chat to your website.',
    loading: 'Loading brands',
    create: 'Create brand',
    createTitle: 'Create a brand',
    name: 'Brand name',
    nameHint: 'Customers see it in the chat.',
    nameRequired: 'Enter a brand name.',
    websites: 'Allowed websites',
    websitesHint:
      'One per line, such as https://shop.example.com. Only these websites can load the chat.',
    websiteInvalid:
      '{{value}} isn’t a website address. Use the form https://shop.example.com, without a path.',
    area: 'Workspace area',
    submit: 'Create brand',
    created: '{{name}} was created.',
    widgetId: 'Widget ID',
    allowed: 'Allowed websites',
    noWebsites: 'No websites are allowed yet, so the chat won’t load anywhere.',
    edit: 'Edit',
    editLabel: 'Edit {{name}}',
    editTitle: 'Edit {{name}}',
    save: 'Save changes',
    saved: '{{name}} was saved.',
    removedWarning:
      'Saving ends the chats open on the websites you took off the list, and the chat stops loading there:',
    empty: {
      title: 'No brands yet',
      description: 'A brand has its own chat widget, allowed websites and AI assistant.',
    },
    chat: {
      title: 'Chat connection',
      account: 'Chatwoot account {{id}}',
      connect: 'Connect Chatwoot',
      connectTitle: 'Connect {{name}} to Chatwoot',
      engineNote:
        'Hamnivo uses Chatwoot as its chat engine. You and your team work in Hamnivo Studio; you don’t need to open Chatwoot.',
      connectIntro: 'Hamnivo sets up a Chatwoot account for this brand with:',
      connectItem1: 'an integration user and an inbox for the website chat,',
      connectItem2: 'an AI bot that answers in that inbox,',
      connectItem3: 'signed webhooks back to Hamnivo.',
      connectNote:
        'The brand’s AI assistant is created as a draft. It doesn’t answer customers until you publish it.',
      connectSubmit: 'Connect',
      connected: '{{name}} is connected to Chatwoot.',
      noPermission: 'You need permission to manage integrations to connect Chatwoot.',
      statusHidden: 'Only people who manage integrations for the whole workspace see this status.',
    },
    install: {
      title: 'Install the chat',
      description:
        'Add this code to each page of an allowed website, just before the closing </body> tag.',
      copy: 'Copy install code',
      copied: 'Install code copied.',
      copyFailed: 'Copy didn’t work. Select the code and copy it yourself.',
      note: 'Copying the code doesn’t connect anything by itself. The chat appears once the code runs on an allowed website.',
    },
  },
  agents: {
    title: 'AI agents',
    description:
      'Each agent answers for one brand. Changes are saved as drafts and go live only when you publish.',
    loading: 'Loading agents',
    columns: {
      answers: 'Where it answers',
      name: 'Agent',
      brand: 'Brand',
      model: 'Model',
      live: 'Live version',
    },
    isDefault: 'Brand default',
    draft: 'v{{version}}',
    notPublished: 'Not published',
    open: 'Open {{name}}',
    create: 'Create agent',
    empty: {
      title: 'No AI agents yet',
      description:
        'Connecting a brand to Chatwoot creates its assistant as a draft. You can also create one here.',
    },
    draftModel: 'Draft: {{model}}',
    noPurpose: 'No purpose written yet.',
    answers: {
      customers:
        'Answers customers of {{brand}} in web chat and on the brand’s channels where the AI is on.',
      assist: 'Suggests replies to your team on {{brand}}’s chats. It never writes to customers.',
      notDefault:
        'Answers no one: only the default agent of {{brand}} answers. Make it the default to use it.',
      notLive: 'Answers no one yet: it has not been published.',
      archived: 'Archived. It answers no one.',
    },
    filters: {
      search: 'Search agents',
      searchHint: 'By name, display name, purpose, brand or model.',
      brand: 'Brand',
      allBrands: 'All brands',
      state: 'Show',
      all: 'All agents',
      answering: 'Answering now',
      idle: 'Not answering',
      count: 'Showing {{shown}} of {{total}} agents.',
      none: 'No agent matches these filters.',
      clear: 'Clear filters',
    },
    createForm: {
      title: 'Create an agent',
      brand: 'Brand',
      name: 'Internal name',
      nameHint: 'Only staff see this name.',
      nameRequired: 'Enter an internal name.',
      brandRequired: 'Choose a brand.',
      makeDefault: 'Make it the brand’s default agent',
      makeDefaultHint: 'The default agent answers the brand’s conversations.',
      submit: 'Create agent',
      created: '{{name}} was created as a draft.',
      noBrands: 'Create a brand first.',
    },
  },
  editor: {
    loading: 'Loading agent',
    allAgents: 'All agents',
    brand: 'Brand: {{name}}',
    notLive: {
      title: 'Saved is not live',
      live: 'Customers get version {{version}} until you publish.',
      none: 'This agent gives customers no answers until you publish a version.',
      draftAhead: 'Draft version {{version}} has changes that aren’t live yet.',
    },
    unsaved: 'Unsaved changes',
    allSaved: 'All changes saved',
    readOnly: 'You can view this agent but not change it.',
    sections: {
      identity: 'Identity',
      behaviour: 'Behaviour',
      model: 'Model',
      limits: 'Limits',
      knowledge: 'Knowledge',
      memory: 'Customer memory',
    },
    displayName: 'Name customers see',
    displayNameHint:
      'Shown next to every AI message. Describe the role, such as “Store assistant”. Don’t use a person’s name.',
    purpose: 'Purpose',
    purposeHint: 'What this agent is for, in a sentence or two.',
    instructions: 'Instructions',
    instructionsHint:
      'Your business rules and style. Hamnivo adds honesty and safety rules that can’t be removed.',
    tone: 'Tone',
    tones: {
      warm: 'Warm',
      warmHint: 'Friendly and conversational.',
      neutral: 'Neutral',
      neutralHint: 'Plain and to the point.',
      formal: 'Formal',
      formalHint: 'Polite and precise.',
    },
    languages: 'Reply languages',
    languagesHint: 'A customer who writes in another language is offered a person.',
    otherLanguages: 'Other language tags',
    otherLanguagesHint: 'Optional. Separate with commas, such as hi-Latn, ur-Latn.',
    mode: 'Mode',
    modes: {
      answer: 'Answer customers',
      answerHint: 'Replies in the chat.',
      assist: 'Assist staff',
      assistHint:
        'Writes a draft reply for your team to approve, edit or reject. Never replies to customers on its own.',
    },
    provider: 'Provider',
    providers: {
      anthropic: 'Anthropic',
      openai: 'OpenAI',
      google: 'Google Gemini',
      'openai-compatible': 'OpenAI-compatible server',
      mock: 'Hamnivo test model (offline)',
    },
    modelId: 'Model ID',
    modelIdHint: 'Exactly as the provider names it.',
    credentials: 'Credentials',
    platformKey: 'Server key (set when Hamnivo starts)',
    credentialsHint: 'Use a saved model connection, or the key the server was started with.',
    connectionsHidden: 'You can’t see model connections, so the current choice is kept.',
    unknownConnection: 'Saved connection {{id}}',
    fallback: 'Fallback models',
    fallbackHint: 'Tried in order when the main model is overloaded or unavailable. Up to 2.',
    fallbackItem: 'Fallback {{n}}',
    addFallback: 'Add fallback model',
    removeFallback: 'Remove fallback {{n}}',
    limits: {
      hint: 'When a run reaches a limit, it stops and hands the conversation to a person.',
      maxOutputTokens: 'Longest answer (tokens)',
      timeout: 'Time limit (seconds)',
      historyMessages: 'Messages of history to read',
      maxSteps: 'Most steps in one run',
    },
    knowledge: {
      hint: 'Customer answers use only public sources.',
      none: 'This brand has no knowledge sources yet.',
      manage: 'Manage knowledge',
      hidden: 'You can’t see knowledge sources.',
    },
    memory: {
      enabled: 'Remember customer preferences',
      hint: 'Only for customers who agree in the chat. They can see, correct and delete what’s remembered, and turn memory off.',
      maxItems: 'Most memories in one answer',
      maxChars: 'Most characters of memory in one answer',
    },
    save: 'Save draft',
    saved: 'Draft saved as version {{version}}.',
    discard: 'Discard changes',
    publish: 'Publish version {{version}}',
    publishNone: 'Publish',
    publishWhy: {
      unsaved: 'Save the draft before publishing.',
      noDraft: 'There’s no new draft to publish.',
      noPermission: 'You need permission to publish agents.',
      archived: 'Archived agents can’t be published.',
    },
    publishConfirm: {
      title: 'Publish version {{version}}?',
      body: 'Customers get answers from version {{version}} right away.',
      replaces: 'Version {{version}} stops answering.',
      first: 'This is the first version customers will see.',
      submit: 'Publish',
    },
    published: 'Version {{version}} is live.',
    publishFailed: 'Version {{version}} wasn’t published. The live version didn’t change.',
    conflict: {
      title: 'Someone saved a newer draft',
      body: 'Version {{version}} was saved while you were editing. Your changes are still here.',
      differences: 'Fields that differ from the newer draft:',
      same: 'The newer draft has the same values as yours.',
      loadTheirs: 'Load the newer draft',
      keepMine: 'Keep my changes',
      keepMineHint: 'Saves your version over the newer draft.',
    },
    versions: {
      title: 'Versions',
      loading: 'Loading versions',
      state: { draft: 'Draft', published: 'Live', retired: 'Retired' },
      version: 'Version {{version}}',
      hash: 'Hash',
      noHash: 'Fixed when published',
      created: 'Created {{time}}',
      publishedAt: 'Published {{time}}',
      rollback: 'Roll back to version {{version}}',
      rollbackTitle: 'Roll back to version {{version}}?',
      rollbackBody:
        'Version {{version}} becomes live again. Your draft is kept and can be published later.',
      rollbackSubmit: 'Roll back',
      rolledBack: 'Version {{version}} is live again.',
      empty: 'No versions yet.',
    },
    leave: {
      title: 'Leave without saving?',
      description: 'Your changes to this agent aren’t saved.',
      save: 'Save draft',
      discard: 'Discard',
      stay: 'Stay',
    },
    errors: {
      summary: 'Fix these fields to save',
      displayName: 'Enter the name customers see, up to 60 characters.',
      purpose: 'Keep the purpose under 2,000 characters.',
      instructions: 'Keep the instructions under 20,000 characters.',
      languages: 'Choose at least one language, and no more than 20.',
      languageTag: '{{tag}} isn’t a language tag. Use a form such as hi-Latn.',
      modelId: 'Enter a model ID.',
      number: 'Enter a whole number from {{min}} to {{max}}.',
      knowledge: 'Choose no more than 20 sources.',
    },
  },
  models: {
    title: 'Model connections',
    description:
      'API keys for model providers. Keys are stored encrypted and never shown again after you save them.',
    loading: 'Loading model connections',
    add: 'Add connection',
    addTitle: 'Add a model connection',
    provider: 'Provider',
    label: 'Label',
    labelHint: 'For you and your team, such as “OpenAI production”.',
    baseUrl: 'Base URL',
    baseUrlHint: 'Needed for OpenAI-compatible servers. Optional for others (a proxy or region).',
    apiKey: 'API key',
    apiKeyHint: 'Stored encrypted. You won’t see it again, only its last characters.',
    allowedModels: 'Allowed models',
    allowedModelsHint: 'Optional. One model ID per line. Leave empty to allow any model.',
    submit: 'Save connection',
    added:
      'Connection saved. The key is stored encrypted and won’t be shown again. It ends in {{hint}}.',
    columns: {
      label: 'Label',
      provider: 'Provider',
      key: 'Key',
      models: 'Allowed models',
      status: 'Status',
      created: 'Added',
      actions: 'Actions',
    },
    anyModel: 'Any model',
    keyEnding: 'Ends in {{hint}}',
    status: { active: 'Active', disabled: 'Disabled' },
    disable: 'Disable',
    disableLabel: 'Disable {{label}}',
    disableTitle: 'Disable {{label}}?',
    disableBody:
      'Agents that use it stop answering until you choose other credentials. The stored key is revoked and can’t be recovered.',
    disableSubmit: 'Disable connection',
    disabled: '{{label}} is disabled.',
    empty: {
      title: 'No model connections yet',
      description:
        'Without one, agents can use only the providers the server has keys for, and the offline test model.',
    },
    errors: {
      label: 'Enter a label.',
      apiKey: 'Enter the API key. It has at least 8 characters.',
      baseUrl: 'Enter an address that starts with http:// or https://.',
      baseUrlRequired: 'OpenAI-compatible servers need a base URL.',
    },
  },
  knowledge: {
    title: 'Knowledge',
    description:
      'Sources the AI answers from. Documents are checked and indexed before answers use them.',
    sources: {
      title: 'Sources',
      loading: 'Loading sources',
      add: 'Add source',
      documents: 'Documents: {{total}}',
      review: 'Review every {{days}} days',
      noReview: 'No review cycle',
      settings: 'Settings for {{name}}',
      empty: {
        title: 'Add your first trusted source',
        description: 'Give the AI accurate information from content you already have.',
      },
    },
    visibility: {
      public: 'Public answers',
      internal: 'Team only',
      publicHint: 'Can ground answers to customers.',
      internalHint:
        'Only for your team, such as in knowledge search. Never used in answers or drafts for customers.',
    },
    sourceStatus: { active: 'Active', paused: 'Paused', retired: 'Retired' },
    sourceForm: {
      addTitle: 'Add a knowledge source',
      editTitle: 'Source settings',
      brand: 'Brand',
      name: 'Source name',
      nameRequired: 'Enter a source name.',
      visibility: 'Who may see answers from it',
      review: 'Review cycle in days',
      reviewHint:
        'Optional. Documents not reviewed within this many days stop grounding answers until someone reviews them.',
      reviewInvalid: 'Enter a whole number of days from 1 to 3650, or leave it empty.',
      status: 'Status',
      submit: 'Add source',
      save: 'Save settings',
      created: '{{name}} was added.',
      saved: 'Settings saved.',
    },
    documents: {
      title: 'Documents',
      loading: 'Loading documents',
      add: 'Add document',
      pick: 'Choose a source to see its documents.',
      polling: 'Studio checks for updates every few seconds while documents are being processed.',
      columns: {
        title: 'Document',
        state: 'State',
        freshness: 'Freshness',
        version: 'Version',
      },
      select: 'Show details for {{title}}',
      empty: {
        title: 'No documents in this source yet',
        description:
          'Paste text or upload a file. It is checked and indexed before answers use it.',
      },
    },
    state: {
      queued: 'Queued',
      processing: 'Processing',
      ready: 'Ready',
      failed: 'Failed',
      rejected: 'Rejected',
      superseded: 'Replaced',
      retired: 'Retired',
    },
    freshness: { current: 'Current', stale: 'Needs review', expired: 'Expired' },
    detail: {
      title: 'Document details',
      back: 'Back to documents',
      answering: 'Answering version',
      newest: 'Newest version',
      noneAnswering: 'No version answers yet.',
      version: 'Version {{version}}',
      size: 'Size',
      passages: 'Passages',
      added: 'Added',
      readySince: 'Ready since',
      validUntil: 'Used until',
      reviewed: 'Last reviewed',
      neverReviewed: 'Not reviewed yet',
      reason: 'Reason: {{reason}}',
      original: 'Open the original',
      keepsAnswering: 'Version {{version}} keeps answering until the new one is ready.',
      markReviewed: 'Mark as reviewed',
      markedReviewed: '{{title}} was marked as reviewed.',
      retire: 'Retire',
      retireTitle: 'Retire {{title}}?',
      retireBody: 'Answers stop using this document. It stays in the list as retired.',
      retireSubmit: 'Retire document',
      retired: '{{title}} was retired.',
      newVersion: 'Upload a new version',
    },
    upload: {
      addTitle: 'Add a document',
      versionTitle: 'New version of {{title}}',
      docTitle: 'Title',
      titleRequired: 'Enter a title.',
      url: 'Original address (URL)',
      urlHint: 'Optional. Shown with citations so people can read the original.',
      urlInvalid: 'Enter an address that starts with http:// or https://.',
      language: 'Language tag',
      languageHint: 'Optional, such as en, ar or hi-Latn.',
      languageInvalid: 'Use a language tag such as en or hi-Latn.',
      content: 'Content',
      paste: 'Paste text',
      file: 'Upload a file',
      format: 'Format',
      formats: { markdown: 'Markdown', plain: 'Plain text', html: 'HTML' },
      text: 'Text',
      textRequired: 'Paste the text.',
      fileLabel: 'File',
      fileHint: 'PDF, Word (.docx), HTML, Markdown or plain text, up to 5 MB.',
      fileRequired: 'Choose a file.',
      fileTooLarge: 'This file is {{size}}. Files can be up to 5 MB.',
      fileType:
        'This type of file isn’t supported. Use PDF, Word (.docx), HTML, Markdown or plain text.',
      fileChosen: 'Chosen: {{name}}, {{size}}',
      validUntil: 'Stop using it after',
      validUntilHint: 'Optional, for example for a seasonal policy.',
      submit: 'Add document',
      submitVersion: 'Upload version',
      queued: '{{title}} is queued for checking.',
    },
    search: {
      title: 'Search preview',
      description: 'See which passages an answer would use.',
      brand: 'Brand',
      audience: 'Answer for',
      audiences: {
        customer: 'Customers (public sources only)',
        staff: 'Staff (all sources)',
      },
      query: 'Question',
      queryRequired: 'Enter a question.',
      submit: 'Search',
      results: 'Passages, best first',
      found: 'Passages found: {{total}}',
      score: 'Score {{score}}',
      version: 'Version {{version}}',
      empty: 'No passages match. An answer would ask for more detail or offer a person.',
      noBrands: 'Create a brand to search its knowledge.',
    },
  },
  stopAi: {
    title: 'Stop AI',
    description: 'Stop AI replies at once. People keep chatting, and new messages go to your team.',
    loading: 'Loading stop switches',
    dialogTitle: 'Stop AI replies',
    dialogDescription:
      'The AI stops answering where you choose. Conversations stay open for your team.',
    scope: 'Where to stop',
    scopes: {
      tenant: 'Everywhere in this workspace',
      brand: 'One brand',
      agent: 'One agent',
    },
    brand: 'Brand',
    agent: 'Agent',
    choose: 'Choose…',
    reason: 'Reason',
    reasonHint: 'Your team sees this. For example: “Wrong prices in answers”.',
    submit: 'Stop AI replies',
    stopped: 'AI replies stopped. {{scope}}.',
    errors: {
      reason: 'Give a reason of at least 3 characters.',
      target: 'Choose where to stop the AI.',
    },
    active: { title: 'Active stops', empty: 'The AI isn’t stopped anywhere.' },
    past: { title: 'Earlier stops', empty: 'No earlier stops.' },
    columns: {
      since: 'Stopped',
      by: 'By',
      cleared: 'Resumed',
    },
    status: { active: 'AI stopped', cleared: 'Resumed' },
    clear: 'Let AI answer again',
    clearLabel: 'Let AI answer again: {{scope}}',
    clearTitle: 'Let the AI answer again?',
    clearBody: 'AI replies resume for: {{scope}}. Check that the reason for stopping is fixed.',
    clearSubmit: 'Let AI answer',
    cleared: 'AI replies resumed. {{scope}}.',
    noPermission: 'You need permission to publish agents to stop or resume the AI.',
    you: 'You',
    teammate: 'A teammate',
    unknownTarget: 'Unknown ({{id}})',
  },
  runs: {
    title: 'Runs',
    description:
      'What each AI run did: outcome, version, model, tokens and time. Prompts and replies aren’t kept here.',
    loading: 'Loading runs',
    filters: {
      label: 'Filter runs',
      agent: 'Agent',
      allAgents: 'All agents',
      outcome: 'Outcome',
      allOutcomes: 'All outcomes',
      conversation: 'Conversation ID',
      conversationHint: 'Optional, starts with conv_',
      apply: 'Apply filters',
      reset: 'Reset filters',
      outcomeNote: 'The outcome filter applies to the runs loaded on this page.',
    },
    columns: {
      started: 'Started',
      outcome: 'Outcome',
      agent: 'Agent and version',
      model: 'Model',
      tokens: 'Tokens in / out',
      latency: 'Time taken',
      details: 'Details',
    },
    outcome: {
      replied: 'Replied',
      drafted: 'Drafted for staff',
      handoff: 'Handed to a person',
      fenced: 'Discarded, conversation moved on',
      blocked: 'Blocked by a guard',
      error: 'Error',
      skipped: 'Skipped',
      cancelled: 'Stopped by a person',
      running: 'Running',
    },
    view: 'View',
    viewLabel: 'View the run started {{time}}',
    loadMore: 'Load more runs',
    shown: 'Runs shown: {{total}}',
    empty: {
      title: 'No runs yet',
      description: 'Runs appear when the AI answers or drafts in a conversation.',
      action: 'Open AI agents',
    },
    noMatches: {
      title: 'No runs match these filters',
      description: 'Remove a filter to see more runs.',
    },
    detail: {
      title: 'Run details',
      loading: 'Loading run',
      summary: 'Summary',
      states: {
        running: 'Running',
        succeeded: 'Succeeded',
        failed: 'Failed',
        skipped: 'Skipped',
      },
      kind: 'Kind',
      kinds: { reply: 'Reply to customer', draft: 'Draft for staff', guard: 'Message guard' },
      conversation: 'Conversation',
      started: 'Started',
      finished: 'Finished',
      errorKind: 'Error kind',
      steps: 'Steps',
      noSteps: 'No steps were recorded.',
      step: 'Step {{n}}',
      stepKinds: {
        model: 'Model',
        guard: 'Guard',
        retrieval: 'Knowledge search',
        decision: 'Decision',
        tool: 'Tool',
      },
      stepStatus: { ok: 'OK', error: 'Error', blocked: 'Blocked', skipped: 'Skipped' },
      noDetail: 'No further details.',
      version: 'v{{version}}',
      noAgent: 'No agent',
    },
  },
  inbox: {
    title: 'Inbox',
    documentTitle: '{{name}} · Inbox',
    loading: 'Loading conversations',
    conversationLoading: 'Loading the conversation',
    loadMore: 'Show more conversations',
    shown: '{{n}} conversations shown',
    unavailable: 'This conversation isn’t available',
    notFound: 'It may be in a workspace or brand you can’t see, or the link is wrong.',
    back: 'Back to the inbox',
    forbidden: {
      title: 'You can’t see conversations here',
      description: 'Ask a workspace owner or administrator for a role that includes the inbox.',
    },
    queues: {
      label: 'Queues',
      mine: 'Mine',
      unassigned: 'Waiting for a person',
      overdue: 'Overdue',
      ai: 'AI answering',
      team: 'My teams',
      tickets: 'Tickets',
      all: 'All',
    },
    tickets: ticketsEn,
    sla: slaEn,
    aiDrafts: aiDraftsEn,
    offers: offersEn,
    blocking: blockingEn,
    keys: {
      plain: 'Keys: ↑ ↓ or J K to move, Enter to open, Esc to come back.',
      withSelect: 'Keys: ↑ ↓ or J K to move, Enter to open, X to select, Esc to come back.',
    },
    search: {
      label: 'Search conversations and messages',
      placeholder: 'Search names, emails or message text',
      submit: 'Search',
      loading: 'Searching',
      tooShort: 'Type at least 2 characters.',
      found: '{{conversations}} conversations and {{messages}} messages found.',
      conversations: 'Customers',
      messages: 'Messages',
      none: 'Nothing found',
      noneHint: 'Only conversations you can see are searched.',
      clear: 'Clear search',
      more: 'Show more messages',
    },
    filters: {
      button: 'Filters',
      buttonCount: 'Filters ({{n}})',
      title: 'Filter the queue',
      close: 'Close filters',
      status: 'Status',
      statuses: {
        active: 'Open or pending',
        snoozed: 'Snoozed',
        resolved: 'Resolved',
        all: 'Any status',
      },
      brand: 'Brand',
      allBrands: 'All brands',
      channel: 'Channel',
      allChannels: 'All channels',
      language: 'Customer language',
      allLanguages: 'All languages',
      tag: 'Tag',
      allTags: 'All tags',
      active: 'Filters on: {{n}}',
      clear: 'Clear filters',
    },
    empty: {
      filteredTitle: 'No conversations match these filters',
      filteredDescription: 'Clear the filters to see the whole queue.',
      seeAll: 'See all conversations',
      overview: 'Go to the overview',
      mine: {
        title: 'Nothing assigned to you',
        description: 'Conversations you take over or are given show up here.',
      },
      unassigned: {
        title: 'Nobody is waiting for a person',
        description: 'When a customer asks for a person, the conversation waits here.',
      },
      overdue: {
        title: 'Nothing is overdue',
        description:
          'Conversations past their reply-time target show up here, the most overdue first.',
      },
      ai: {
        title: 'The AI isn’t answering anyone right now',
        description: 'Conversations the AI is answering show up here.',
      },
      team: {
        title: 'Nothing in your teams',
        description: 'Conversations given to your teams show up here.',
      },
      tickets: {
        title: 'No tickets yet',
        description:
          'Messages left with the contact form while nobody could answer become tickets. So does any conversation you make a ticket.',
      },
      all: {
        title: 'No conversations yet',
        description: 'Conversations appear here when customers write through a connected brand.',
      },
    },
    pick: {
      title: 'Choose a conversation',
      description: 'Open one from the queue to read it and reply.',
    },
    connection: {
      reconnecting:
        'Reconnecting… Updates may be late; the inbox checks every 15 seconds. Your drafts are saved.',
      offline: 'You’re offline. Your drafts are kept in this browser until you’re back.',
    },
    row: {
      noMessages: 'No messages yet',
      team: 'Team: {{name}}',
      from: { ai: 'AI:', staff: 'Staff:', system: 'Note:' },
    },
    owner: {
      ai: 'AI answering',
      waiting: 'Waiting for a person',
      waitingFor: 'Waiting {{time}}',
      paused: 'AI paused',
      you: 'You',
      person: 'A person',
      external: 'Someone in Chatwoot',
    },
    status: {
      open: 'Open',
      pending: 'Pending',
      snoozed: 'Snoozed',
      snoozedUntil: 'Snoozed until {{time}}',
      resolved: 'Resolved',
      resolve: 'Resolve',
      resolvedDone: 'Conversation resolved.',
      reopen: 'Reopen',
      wake: 'Unsnooze',
      reopened: 'Conversation reopened. You’re handling it now.',
    },
    identity: {
      verified: 'Verified customer',
      anonymous: 'Not signed in',
      unknown: 'Unknown customer',
    },
    channel: {
      chat: 'Website chat',
      email: 'Email',
      whatsapp: 'WhatsApp',
      telegram: 'Telegram',
      sms: 'SMS',
      other: 'Other channel',
    },
    details: {
      title: 'Details',
      show: 'Details',
      hide: 'Hide details',
    },
    banner: {
      ai: '{{name}} is answering. Replying or taking over pauses the AI in this conversation.',
      other: '{{name}} is handling this conversation.',
      otherSince: '{{name}} is handling this conversation. They took it {{time}}.',
      notesStill: 'You can still add internal notes.',
      asked: 'The customer asked for a person.',
      askedSince: 'The customer asked for a person {{time}}.',
      released: 'This conversation was put back in the queue.',
      channelAiOff: 'The AI does not answer on this channel, so a person needs to answer.',
      waiting: 'This conversation is waiting for a person.',
      takeOver: 'Take over',
      claim: 'Assign to me',
      claimLost: 'Someone else took this conversation first. The view now shows who has it.',
    },
    presence: {
      viewing: '{{name}} is viewing',
      typingReply: '{{name}} is typing a reply',
      typingNote: '{{name}} is typing a note',
      alsoReply: '{{name}} is also typing a reply here.',
      alsoNote: '{{name}} is also typing a note here.',
    },
    assign: {
      button: 'Assign',
      me: 'Assign to me',
      colleagues: 'Colleagues who can reply',
      current: 'Has it now',
      queue: 'Back to the queue',
      ai: 'Hand back to the AI',
      team: 'Team',
      teamCurrent: 'Team: {{name}}',
      noTeams: 'No teams yet',
      noTeam: 'No team',
      doneMe: 'You’re handling this conversation now.',
      doneTo: 'Given to {{name}}.',
      doneQueue: 'Put back in the queue.',
      doneAi: 'The AI is answering again.',
      doneTeam: 'Given to the {{name}} team.',
      doneNoTeam: 'Taken out of its team.',
    },
    transfer: {
      ask: 'Ask to take over',
      title: 'Ask to take over',
      description:
        '{{name}} gets an internal note asking to hand this conversation to you. Nothing changes until they or a supervisor do it.',
      note: 'Why (for your colleague)',
      send: 'Send request',
      sent: 'Request sent to {{name}}.',
    },
    snooze: {
      button: 'Snooze',
      hour: 'For 1 hour',
      tomorrow: 'Until tomorrow ({{time}})',
      nextWeek: 'Until next week ({{time}})',
      pick: 'Choose a time…',
      pickTitle: 'Snooze until',
      pickDescription:
        'The conversation comes back to the queue at this time, or when the customer writes.',
      until: 'Date and time',
      future: 'Choose a time in the future.',
      confirm: 'Snooze',
      done: 'Snoozed until {{time}}.',
    },
    timeline: {
      label: 'Messages',
      loading: 'Loading messages',
      older: 'Show earlier messages',
      empty: 'No messages yet.',
    },
    roles: {
      customer: 'Customer',
      staff: 'Support agent',
      you: 'You',
      ai: 'AI assistant',
      specialist: 'AI specialist',
      guest: 'Guest',
      system: 'System',
    },
    lanes: {
      public: 'Reply',
      publicDescription: 'The customer sees replies.',
      internal: 'Internal note',
      internalDescription: 'Only your team sees notes. The customer never does.',
    },
    message: {
      handoffSummary: 'Handoff summary',
      staffOnly: 'Staff only',
      sources: 'Sources',
      newFrom: 'New message from {{name}}.',
      file: {
        quarantined: 'waiting for a check',
        scanning: 'being checked',
        available: 'available',
        rejected: 'blocked',
      },
      place: 'Shared a place: {{label}}',
      contact: 'Shared a contact: {{details}}',
      reaction: 'Reacted with {{emoji}}',
    },
    translation: {
      translate: 'Translate to {{language}}',
      label: 'Machine translation to {{language}}. The original is above.',
      hide: 'Hide',
      show: 'Show',
      failed: 'The translation didn’t work. The original message is unchanged.',
    },
    composer: {
      lanes: 'Write a reply or a note',
      replyLabel: 'Reply to {{name}}',
      noteLabel: 'Internal note',
      replyPlaceholder: 'Write a reply to {{name}}…',
      notePlaceholder: 'Write a note for your team…',
      send: 'Send',
      addNote: 'Add note',
      toSend: 'to send',
      sent: 'Reply sent.',
      noteAdded: 'Note added.',
      notSent: 'Not sent.',
      saving: 'Saving draft…',
      saved: 'Draft saved',
      savedHere: 'Draft kept in this browser',
      offlineSaved: 'Offline: draft kept in this browser',
      hasDraft: '(draft)',
      takesOver: 'Sending a reply assigns this conversation to you.',
      takesOverAi: 'Sending a reply takes over from the AI.',
      noReply: 'Your role can read this conversation but not reply.',
      noNote: 'Your role can’t add notes here.',
      ownedByOther: '{{name}} is handling this conversation. Ask to take over before replying.',
      staleSend:
        'Not sent: someone else changed who handles this conversation. Your text is kept below. Check the conversation, then try again.',
      conflict: 'This draft was changed in another tab or device.',
      keepMine: 'Keep mine',
      useTheirs: 'Use the other version',
    },
    outgoing: {
      label: 'Messages not sent yet',
      sending: 'Sending',
      failed: 'Not sent',
      ownership: 'Someone else took over before it was sent. Nothing reached the customer.',
      unavailable: 'Chatwoot didn’t answer. Sending again is safe: it is never sent twice.',
      other: 'It couldn’t be sent. Try again.',
      retry: 'Try again',
      discard: 'Discard',
      discarded: 'Discarded.',
    },
    saved: {
      button: 'Saved replies',
      search: 'Search saved replies',
      none: 'No saved replies found.',
      missing: 'Fill in by hand: {{names}}',
    },
    copilot: {
      button: 'Suggest a reply',
      title: 'Suggested reply',
      review: 'Written by {{name}} (AI). Check it before sending; nothing is sent on its own.',
      noSources: 'No sources were used.',
      use: 'Edit in reply',
      copy: 'Copy',
      sendAsMe: 'Send as me',
      dismiss: 'Dismiss suggestion',
      ready: 'Suggested reply ready.',
      off: 'The AI assistant is turned off for this brand, so it can’t suggest replies.',
    },
    customer: {
      title: 'Customer',
      verifiedHint: 'Signed in on the brand’s website; their identity was checked.',
      anonymousHint: 'Not signed in. Their name and details were not checked.',
      email: 'Email',
      unverifiedEmail: 'Email given',
      notVerified: '(not verified)',
      externalId: 'Customer ID',
      language: 'Language',
      brand: 'Brand',
      since: 'Customer since',
      lastSeen: 'Last seen',
      previous: 'Other conversations',
      noPrevious: 'No other conversations you can see.',
    },
    tags: {
      title: 'Tags',
      none: 'No tags yet.',
      add: 'Add a tag',
      choose: 'Choose a tag',
      addButton: 'Add',
      remove: 'Remove tag {{name}}',
      added: 'Tag {{name}} added.',
      removed: 'Tag {{name}} removed.',
      noneDefined: 'No tags are set up for this brand yet.',
    },
    fields: {
      title: 'Custom fields',
      none: 'No custom fields are set up for this brand.',
      save: 'Save fields',
      saved: 'Fields saved.',
    },
    links: {
      title: 'Linked conversations',
      none: 'No linked conversations.',
      other: 'Conversation ID to link',
      otherHint: 'For example conv_…; both conversations stay separate.',
      note: 'Why they are linked',
      add: 'Link',
      added: 'Conversations linked.',
      remove: 'Remove the link to {{name}}',
      removed: 'Link removed.',
    },
    reviews: {
      title: 'Supervisor review',
      none: 'No reviews yet.',
      rating: '{{rating}} of 5',
      ratingLabel: 'Rating',
      note: 'Coaching note',
      save: 'Save review',
      saved: 'Review saved.',
      by: 'By {{reviewer}}',
      byAbout: 'By {{reviewer}}, about {{subject}}',
    },
    bulk: {
      select: 'Select {{name}}',
      selected: '{{name}} selected. Selected: {{n}}.',
      unselected: '{{name}} not selected. Selected: {{n}}.',
      count: 'Selected: {{n}}',
      clear: 'Clear selection',
      assignMe: 'Assign to me',
      resolve: 'Resolve',
      reopen: 'Reopen',
      tagLabel: 'Tag to add',
      tagPlaceholder: 'Tag to add',
      addTag: 'Add tag',
      confirmTitle: 'Change the selected conversations?',
      willChange: 'Can be changed: {{n}}.',
      cannotChange: 'Can’t be changed: {{n}}.',
      apply: 'Change {{n}}',
      result: 'Done: {{done}}. Failed: {{failed}}.',
      partial: 'Done: {{done}}. Not changed: {{failed}}. Only those can be tried again.',
      retryFailed: 'Try the failed ones again',
      failed: 'Not changed',
    },
  },
  account: {
    title: 'Account security',
    description: 'How you sign in to Studio.',
    twoFactor: {
      title: 'Two-factor authentication',
      on: 'On',
      off: 'Off',
      description:
        'Studio asks for a code from an authenticator app when you sign in. Some actions need it.',
      enable: 'Turn on',
      disable: 'Turn off',
      password: 'Your password',
      passwordHint: 'Enter your password to change this setting.',
      setupTitle: 'Add Hamnivo to your authenticator app',
      setupBody: 'Enter this setup key in your app, or open the link on this device.',
      secret: 'Setup key',
      openApp: 'Open in authenticator app',
      backupTitle: 'Save your backup codes',
      backupBody:
        'Each code signs you in once if you lose your phone. Store them somewhere safe. They are shown only now.',
      copyCodes: 'Copy backup codes',
      verify: 'Verify and turn on',
      enabled: 'Two-factor authentication is on.',
      disabled: 'Two-factor authentication is off.',
      disableBody:
        'Sign-in will ask only for your password. Actions that need two-factor authentication will stop working for you.',
      wrongPassword: 'That password isn’t right.',
    },
    passkeys: {
      title: 'Passkeys',
      description: 'Sign in with your device’s screen lock instead of a password.',
      add: 'Add a passkey',
      added: 'Passkey added.',
      failed: 'The passkey wasn’t added. Try again.',
      unsupported: 'This browser can’t use passkeys.',
      none: 'No passkeys yet.',
      created: 'Added {{time}}',
      unnamed: 'Passkey',
      loading: 'Loading passkeys',
    },
  },
  decisions: decisionsEn,
  checks: checksEn,
  caching: cachingEn,
  tools: toolsEn,
  commerce: commerceEn,
  developer: developerEn,
  reports: reportsEn,
  privacy: privacyEn,
  chatSettings: chatSettingsEn,
  chatWidget: chatWidgetEn,
  channels: channelsEn,
  automations: automationsEn,
  memory: {
    title: 'Customer memory',
    description: 'Manage what we remember about your customers.',
    allCustomers: 'All customers',
    loading: 'Loading customer memory',
    notOffered: 'This brand’s AI agent doesn’t use memory now, so nothing here is used in replies.',
    exported: 'Memory exported.',
    forbidden: {
      title: 'You can’t see customer memory',
      description: 'Ask an admin of this workspace for access to customer memory.',
    },
    list: {
      title: 'Customers',
      search: 'Search customers',
      searchHint: 'Name, email or customer ID.',
      searchLabel: 'Search customer memory',
      submit: 'Search',
      brand: 'Brand',
      allBrands: 'All brands',
      loading: 'Loading customers',
      toReview: '{{total}} to review',
      columns: {
        customer: 'Customer',
        brand: 'Brand',
        choice: 'Memory',
        remembered: 'Remembered',
        review: 'Needs review',
        changed: 'Last change',
      },
      empty: {
        title: 'No customer memory yet',
        description:
          'Customers appear here after they agree to be remembered in the chat. Turn memory on in an AI agent’s settings.',
        action: 'Open AI agents',
      },
      noMatch: {
        title: 'No customers match',
        description: 'Try another name, email or customer ID.',
      },
    },
    customer: {
      anonymous: 'Anonymous customer',
      unnamed: 'Signed-in customer',
      verified: 'Signed in',
      notVerified: 'Not signed in',
      since: 'Customer since {{date}}',
    },
    consent: {
      granted: 'Memory on',
      declined: 'Declined',
      withdrawn: 'Memory off',
      undecided: 'Not asked yet',
      history: 'Choice history',
      by: {
        customer: 'by the customer',
        staff: 'by staff',
      },
      line: {
        granted: 'Agreed on {{date}}',
        declined: 'Declined on {{date}}',
        withdrawn: 'Turned off on {{date}}',
        undecided: 'The customer hasn’t chosen yet.',
      },
    },
    status: {
      active: 'Current',
      pending: 'Needs review',
      superseded: 'Replaced',
      outdated: 'Outdated',
    },
    sources: {
      customer_statement: 'Customer message',
      customer_control: 'Set by the customer',
      staff_entry: 'Entered by staff',
      agent_inference: 'Inferred',
      system_record: 'System record',
      import: 'Imported',
    },
    writers: {
      ai: 'AI assistant',
      staff: 'Staff member',
      customer: 'The customer',
      system: 'Hamnivo',
    },
    reasons: {
      extracted_from_message: 'From a customer message',
      customer_correction: 'Corrected by the customer',
      customer_confirmation: 'Confirmed by the customer',
      staff_correction: 'Corrected by staff',
      staff_confirmation: 'Confirmed by staff',
    },
    scope: {
      short: {
        person: 'This customer',
        conversation: 'This chat only',
      },
      long: {
        person: 'This customer, in every chat with this brand',
        conversation: 'Only the chat it came from',
      },
    },
    slots: {
      preferred_language: {
        label: 'Preferred language',
        purpose: 'Reply in the customer’s language',
      },
      preferred_name: {
        label: 'Preferred name',
        purpose: 'Address the customer as they asked',
      },
      contact_method: {
        label: 'Contact preference',
        purpose: 'Use the contact method they prefer',
      },
      contact_time: {
        label: 'Best time to reach',
        purpose: 'Contact them when it suits them',
      },
      communication_style: {
        label: 'Reply style',
        purpose: 'Reply the way they prefer',
      },
      delivery_note: {
        label: 'Delivery note',
        purpose: 'Ensure smooth delivery',
      },
      product_interest: {
        label: 'Product interest',
        purpose: 'Suggest what they asked about',
      },
      size_preference: {
        label: 'Size',
        purpose: 'Suggest the right size',
      },
      open_issue: {
        label: 'Open request',
        purpose: 'Continue without asking again',
      },
      stated_order_reference: {
        label: 'Order they mentioned',
        purpose: 'Find their order faster',
      },
    },
    tabs: {
      preferences: 'Preferences',
      facts: 'Conversation facts',
      notes: 'Agent working notes',
      internal: 'Internal only',
    },
    table: {
      title: 'What we remember',
      columns: {
        memory: 'Memory',
        purpose: 'Purpose',
        source: 'Source',
        confirmed: 'Last confirmed',
        visibility: 'Visibility',
      },
      noMatch: 'Nothing matches your search.',
      emptyPreferences: 'No preferences remembered.',
      emptyFacts: 'No conversation facts remembered.',
      searchPreferences: 'Search customer preferences…',
      searchFacts: 'Search conversation facts…',
      notes:
        'The assistant keeps no working notes about customers. It remembers only what customers tell it after they agree.',
    },
    panel: {
      pick: 'Choose a memory to see where it came from.',
      onlyApproved: 'Only approved values are used in customer replies.',
      value: 'Current value',
      status: 'Status',
      purpose: 'Purpose',
      source: 'Source',
      messages: 'Message:',
      conversation: 'Conversation:',
      recordedBy: 'Recorded by',
      note: 'Reason for change',
      scope: 'Scope',
      inEffect: 'In effect since',
      expires: 'Expires',
      noExpiry: 'No end date',
      verified: 'Checked',
      notVerified: 'Not verified. This is what the customer said.',
      version: 'Version',
      edit: 'Edit value',
      newValue: 'New value',
      correctionReason: 'Reason for correction',
      valueRequired: 'Enter the new value.',
      valueSame: 'Enter a value different from the current one.',
      saved: 'Correction saved. The old value stays in the history.',
      unchanged: 'Nothing changed: this value is already remembered.',
      history: 'History',
      historyLine: '{{writer}} · from {{from}} to {{until}}',
    },
    reason: {
      label: 'Reason',
      hint: 'Don’t include personal details. The reason is kept with the change.',
      required: 'Enter a reason.',
    },
    actions: {
      export: 'Export',
      erase: 'Erase all memory',
      outdated: 'Mark outdated',
      delete: 'Delete',
      save: 'Save correction',
    },
    conflict: {
      title: 'A newer message may replace “{{label}}”',
      body: 'There’s a more recent message from {{date}}. Review and confirm which one to keep.',
      bodyNoCurrent: 'A message from {{date}} gave a value that waits for review.',
      customerCan:
        'The customer can also confirm it in the chat. Until then, neither value is used in replies.',
      compare: 'Compare',
      confirm: 'Confirm new value',
      keep: 'Keep current value',
      compareTitle: 'Compare values',
      current: 'Current',
      newer: 'Newer message',
      none: 'None',
    },
    confirmReview: {
      title: 'Confirm the new “{{label}}”?',
      body: 'The new value replaces the current one, which stays in the history.',
      done: 'New value confirmed.',
    },
    reject: {
      title: 'Keep the current value?',
      body: 'The newer value is deleted and not used. A deletion record is kept.',
    },
    outdated: {
      title: 'Mark this memory outdated?',
      body: 'It will no longer be used in replies. It stays in the history until it’s deleted or expires.',
      submit: 'Mark outdated',
      done: 'Marked outdated.',
    },
    delete: {
      title: 'Delete this memory?',
      intro: 'This will remove the following memory from active use:',
      body: 'It will no longer be used in customer replies, but an audit record will be kept.',
      bodyPending: 'The value waiting for review is removed. An audit record will be kept.',
      submit: 'Remove from future use',
    },
    erase: {
      title: 'Erase all memory about {{name}}?',
      body: 'All remembered values ({{total}}) and their history are deleted, with their search indexes.',
      kept: 'The chat history, the audit log and a deletion record are kept.',
      submit: 'Erase all memory',
      turnOff: 'Also turn memory off for this customer',
      turnOffHint: 'Nothing new is remembered until the customer agrees again.',
    },
    notice: {
      removed: 'Removed from active use.',
      erased: 'All memory about this customer was erased.',
      pending: 'Connected indexes are updating. This can take a few minutes.',
      completed: 'Indexes and caches are cleared. The deletion is complete.',
    },
    deletions: {
      title: 'Deletions and audit trail',
      description: 'What was deleted, by whom, and what was kept.',
      steps: 'Deletion steps',
      line: '{{who}} · {{reason}} · {{time}}',
      kept: 'Kept: {{items}}.',
      status: {
        pending: 'In progress',
        completed: 'Complete',
      },
      scope: {
        record: 'Memory removed (records: {{total}})',
        customer: 'All memory erased (records: {{total}})',
      },
      requester: {
        customer: 'The customer',
        staff: 'Staff member',
        system: 'Hamnivo',
      },
      reasons: {
        customer_request: 'Customer request',
        staff_request: 'Staff request',
        erasure_request: 'Erasure request',
        retention_expired: 'Retention period ended',
        review_rejected: 'Rejected in review',
      },
      stepNames: {
        records: 'Records',
        embeddings: 'Search index',
        citations: 'Citations',
        caches: 'Caches',
        verification: 'Verified',
      },
      keptNames: {
        audit_log: 'audit log',
        consent_log: 'memory choices',
        deletion_record: 'deletion record',
        chat_history: 'chat history',
      },
    },
  },
  access: {
    settings: 'Settings',
    you: 'You',
    anyone: 'Anyone',
    copyFailed: 'Copy didn’t work. Select the text and copy it yourself.',
    copyValue: 'Copy {{label}}',
    scope: {
      tenant: 'Whole workspace',
      workspace: 'Workspace: {{name}}',
      brand: 'Brand: {{name}}',
      team: 'Team: {{name}}',
      inbox: 'Inbox',
      unknown: 'Part of the workspace',
    },
    scopeTypes: {
      tenant: 'Whole workspace',
      workspace: 'One workspace area',
      brand: 'One brand',
      team: 'One team',
    },
    team: {
      title: 'Team and access',
      description: 'Manage who can use this workspace, their roles and what they may do.',
      tabs: { members: 'Members', roles: 'Roles', audit: 'Audit log' },
      invite: 'Invite teammate',
      search: 'Search members',
      searchPlaceholder: 'Name or email',
      activeCount: 'Active members: {{n}}',
      loading: 'Loading members',
      noMatches: 'No members match “{{query}}”.',
      clearSearch: 'Clear search',
      columns: {
        name: 'Name',
        role: 'Role',
        teams: 'Teams',
        lastActive: 'Last active',
        status: 'Status',
        actions: 'Actions',
      },
      status: {
        active: 'Active',
        suspended: 'Suspended',
        invited: 'Invite pending',
        expired: 'Invite expired',
        removed: 'Removed',
      },
      noTeams: 'No team',
      noRole: 'No role',
      notYet: 'Not yet',
      invitedOn: 'Invited {{time}}',
      until: 'until {{time}}',
      actionsFor: 'Actions for {{name}}',
      changeAccess: 'Change role or scope',
      suspend: 'Suspend',
      restore: 'Restore access',
      remove: 'Remove from workspace',
      resend: 'Resend invite',
      revokeInvite: 'Revoke invite',
      beyondReach: 'Their access goes beyond yours, so you can’t change it.',
      selfNote: 'You can’t change your own access here.',
      suspendTitle: 'Suspend {{name}}?',
      suspendBody:
        'They lose access to this workspace at once and are signed out if they have no other workspace. Their conversations stay where they are. You can restore them later.',
      suspendSubmit: 'Suspend',
      suspended: '{{name}} is suspended.',
      restoreTitle: 'Restore {{name}}?',
      restoreBody: 'They get back the roles they had. They sign in again to continue.',
      restoreSubmit: 'Restore',
      restored: '{{name}} has access again.',
      revokeInviteTitle: 'Revoke the invitation for {{email}}?',
      revokeInviteBody: 'The link in their email stops working.',
      revokeInviteSubmit: 'Revoke invite',
      inviteRevoked: 'The invitation for {{email}} was revoked.',
      resent: 'A new invitation link was sent to {{email}}. The old link no longer works.',
      recent: 'Recent activity',
      recentLoading: 'Loading recent activity',
      recentEmpty: 'Nothing has happened yet.',
      viewAudit: 'Open the audit log',
    },
    inviteDialog: {
      title: 'Invite teammate',
      description: 'They get an email with a link to join this workspace.',
      email: 'Email address',
      emailInvalid: 'Enter an email address such as name@example.com.',
      role: 'Role',
      roleBeyond: '{{name}} (beyond your access)',
      where: 'Where the role applies',
      workspace: 'Workspace area',
      team: 'Team',
      brand: 'Brand',
      pick: 'Choose one',
      pickRequired: 'Choose where the role applies.',
      expires: 'Access ends',
      expiresHint: 'Leave empty to keep the access until someone changes it.',
      submit: 'Send invite',
      sent: 'Invitation sent to {{email}}. The link works for 7 days.',
      copyLink: 'Copy invitation link',
      linkHint: 'If email isn’t set up on this server, send them this link yourself.',
      done: 'Done',
    },
    changeDialog: {
      title: 'Access for {{name}}',
      description:
        'Each role applies where it was given. Changes take effect on their next request.',
      current: 'Current roles',
      none: 'No roles. They can sign in but can’t do anything here.',
      change: 'Change',
      removeRole: 'Remove role',
      removeRoleLabel: 'Remove {{role}} from {{name}}',
      removeRoleTitle: 'Remove {{role}} from {{name}}?',
      removeRoleBody: 'They lose what this role allows on their next request.',
      add: 'Add another role',
      editTitle: 'Change {{role}}',
      addTitle: 'Add a role',
      reason: 'Reason',
      reasonHint: 'Saved in the audit log.',
      save: 'Save access',
      saved: 'Access for {{name}} was changed.',
      back: 'Back to roles',
    },
    offboard: {
      title: 'Remove {{name}}?',
      loading: 'Checking what they hold',
      body: 'They lose access to this workspace at once. Their roles are removed, they leave their teams and pending invitations to their address stop working. Their past work stays in the history.',
      conversations: 'Open conversations assigned to them: {{n}}.',
      noConversations: 'They have no open conversations.',
      reassign: 'Reassign their open conversations first',
      reassignTo: 'Reassign to',
      queue: 'Back to the team queue',
      cantReassign:
        'You can’t reassign conversations. They wait for them until someone with access reassigns them.',
      reason: 'Reason',
      reasonHint: 'Saved in the audit log.',
      submit: 'Remove {{name}}',
      reassigning: 'Reassigning conversations',
      reassigned: 'Conversations reassigned: {{n}}.',
      reassignFailed: 'Conversations that couldn’t be reassigned: {{n}}. {{name}} was not removed.',
      removed: '{{name}} was removed from this workspace.',
      ownerTitle: 'Assign another owner before removing this owner',
      ownerBody:
        'A workspace must always have at least one owner. Make someone else an owner first, then try again.',
      goToMembers: 'Go to members',
    },
    roles: {
      intro:
        'Built-in roles cover common jobs. Custom roles give exactly the permissions you choose.',
      new: 'New role',
      loading: 'Loading roles',
      builtIn: 'Built-in',
      custom: 'Custom',
      members: 'Members: {{n}}',
      permissions: 'Permissions: {{n}}',
      open: 'Open {{name}}',
      readOnly: 'Read only',
      editorNew: 'New role',
      summary: '{{kind}} · {{members}}',
      name: 'Name',
      key: 'Key',
      keyHint: 'Lowercase letters, digits and _, used in the API. It can’t change later.',
      keyInvalid: 'Use 2–40 lowercase letters, digits or _, starting with a letter.',
      nameRequired: 'Enter a name.',
      description: 'What this role is for',
      permissionsLegend: 'Permissions',
      pickOne: 'Choose at least one permission.',
      notGrantable: 'Beyond your access',
      stepUp: 'Needs a recent sign-in',
      mfa: 'Needs two-factor authentication',
      presetNote:
        'Built-in roles can’t be changed. Duplicate one to make a custom role you can adjust.',
      beyondNote: 'This role includes access you don’t hold, so you can’t change it.',
      duplicate: 'Duplicate as custom role',
      copyName: '{{name}} (copy)',
      preview: 'What saving changes',
      previewLoading: 'Checking the change',
      previewNothing: 'Nothing changes yet.',
      adds: 'Adds',
      removes: 'Removes',
      sensitive: 'Needs extra checks: {{list}}',
      outside: 'You can’t include: {{list}}',
      impact: 'Who is affected',
      impactNone: 'Nobody holds this role yet.',
      gains: 'gains {{list}}',
      loses: 'loses {{list}}',
      unchanged: 'no change',
      save: 'Save changes',
      create: 'Create role',
      saved: 'Role {{name}} was saved.',
      created: 'Role {{name}} was created.',
      conflict:
        'Someone else changed this role while you were editing. Compare with their version: your changes stay, and the preview shows what saving would change in theirs.',
      reload: 'Compare with their version',
      archive: 'Retire role',
      archiveTitle: 'Retire {{name}}?',
      archiveBody:
        'It disappears from the list. Past grants and the audit log keep its name. Only a role nobody holds can be retired.',
      archiveSubmit: 'Retire role',
      archived: 'Role {{name}} was retired.',
      heldNote: 'People hold this role. Change their access before retiring it.',
      close: 'Close role',
      discardTitle: 'Discard your changes to this role?',
      discardBody: 'What you changed here isn’t saved yet.',
    },
    audit: {
      intro:
        'Every change to access, settings and data, in order. Entries can’t be edited or removed.',
      loading: 'Loading the audit log',
      filters: 'Filters',
      action: 'Action',
      actionPlaceholder: 'e.g. member.removed',
      actor: 'Who',
      target: 'Target ID',
      targetPlaceholder: 'e.g. stf_…',
      from: 'From',
      to: 'To',
      apply: 'Apply filters',
      clear: 'Clear filters',
      applied: 'Showing: {{list}}',
      empty: 'No entries match these filters.',
      columns: {
        time: 'Time',
        actor: 'Who',
        action: 'What',
        target: 'Target',
        outcome: 'Outcome',
        details: 'Details',
      },
      outcomes: { success: 'Done', denied: 'Denied', failed: 'Failed' },
      actorTypes: {
        staff: 'Staff member',
        service_account: 'API key',
        customer: 'Customer',
        ai: 'AI',
        system: 'Hamnivo',
        platform: 'Hamnivo support',
      },
      reason: 'Reason: {{reason}}',
      showData: 'Show recorded data',
      loadMore: 'Load older entries',
      verify: 'Check integrity',
      verifyOk: 'The audit log is intact. Entries checked: {{n}}.',
      verifyBroken: 'The audit log is broken at entry {{seq}}. Contact support with this number.',
      export: 'Export',
      exportCsv: 'Export as CSV (spreadsheets)',
      exportNdjson: 'Export as NDJSON (with hashes)',
      exporting: 'Preparing the export',
      exported: 'The export was downloaded. It is recorded in the audit log.',
      exportNote: 'Exports follow the filters above and are recorded in the audit log.',
      forbiddenExport: 'Exporting needs the audit export permission.',
    },
    auditActions: {
      tenant: { created: 'Workspace created' },
      member: {
        suspended: 'Member suspended',
        restored: 'Member restored',
        removed: 'Member removed',
        sessions_ended: 'Sessions ended',
        joined_by_sso: 'Joined with single sign-on',
        sso_join_skipped: 'Single sign-on join skipped',
      },
      invitation: {
        created: 'Invitation sent',
        resent: 'Invitation sent again',
        revoked: 'Invitation revoked',
        accepted: 'Invitation accepted',
      },
      grant: { created: 'Role given', revoked: 'Role removed', replaced: 'Role changed' },
      role: { created: 'Role created', updated: 'Role changed', archived: 'Role retired' },
      audit: { exported: 'Audit log exported' },
      sso: {
        domain_added: 'Domain added',
        domain_verified: 'Domain verified',
        domain_check_failed: 'Domain check failed',
        domain_removed: 'Domain removed',
        connection_created: 'Single sign-on connected',
        connection_updated: 'Single sign-on changed',
        connection_removed: 'Single sign-on removed',
        sign_in_refused: 'Single sign-on refused',
      },
      operator: {
        appointed: 'Platform operator named',
        role_changed: 'Platform operator role changed',
        disabled: 'Platform operator disabled',
      },
      incident: { declared: 'Incident declared', resolved: 'Incident resolved' },
      support: {
        requested: 'Support asked for access',
        approved: 'Support access approved',
        declined: 'Support access declined',
        revoked: 'Support access revoked',
        ended: 'Support access ended',
        accessed: 'Support read data',
      },
    },
    security: {
      title: 'Security',
      description:
        'Single sign-on for your email domain, and temporary access for Hamnivo support.',
      sso: {
        title: 'Single sign-on',
        description:
          'People with an address at your domain sign in through your identity provider (OpenID Connect).',
        loading: 'Loading single sign-on',
        forbidden: 'Single sign-on is managed by workspace owners and administrators.',
        steps: 'Prove you own the domain, connect your identity provider, then turn it on.',
        domains: 'Email domains',
        addDomain: 'Add domain',
        domain: 'Domain',
        domainHint: 'The part after @ in your team’s addresses, such as northline.example.',
        domainInvalid: 'Enter a domain such as northline.example.',
        noDomains: 'No domains yet.',
        verified: 'Verified',
        pending: 'Waiting for the DNS record',
        publish:
          'Publish this TXT record at your DNS provider, then check it. Records can take a while to appear.',
        recordName: 'Name',
        recordValue: 'Value',
        check: 'Check now',
        checked: 'Last checked {{time}}',
        results: {
          verified: 'The record was found. The domain is verified.',
          not_found: 'The record wasn’t found yet. Check the name and value, then try again later.',
          lookup_failed: 'The DNS lookup failed. Try again in a few minutes.',
          claimed_elsewhere:
            'Another workspace has already verified this domain. Contact Hamnivo support if that is wrong.',
        },
        removeDomain: 'Remove domain',
        removeDomainLabel: 'Remove {{domain}}',
        removeDomainTitle: 'Remove {{domain}}?',
        removeDomainBody: 'You can add it again later, but you’ll have to verify it again.',
        connection: 'Identity provider for {{domain}}',
        connectHint: 'Create an OpenID Connect app at your provider, then enter its details here.',
        issuer: 'Issuer URL',
        issuerHint: 'For example https://login.example.com. Hamnivo reads its discovery document.',
        issuerInvalid: 'Enter the issuer URL, starting with https://.',
        clientId: 'Client ID',
        clientSecret: 'Client secret',
        clientSecretHint: 'Stored encrypted. It is never shown again, only its last characters.',
        required: 'Fill this in.',
        save: 'Save connection',
        created:
          'Connected. Register the redirect URI at your provider, then turn the connection on.',
        redirectUri: 'Redirect URI',
        redirectUriHint: 'Register this address at your identity provider.',
        secretMasked: 'Client secret {{masked}}',
        rotated: 'Replaced {{time}}',
        replaceSecret: 'Replace client secret',
        replaceTitle: 'Replace the client secret',
        replaceSubmit: 'Replace secret',
        replaced: 'The client secret was replaced.',
        endpoints: 'Endpoints found at the provider',
        status: { draft: 'Not on yet', active: 'On', disabled: 'Off' },
        turnOn: 'Turn on',
        turnOff: 'Turn off',
        turnedOn: 'Single sign-on is on for {{domain}}.',
        turnedOff: 'Single sign-on is off for {{domain}}. Passwords work again.',
        ssoOnly: 'Require single sign-on',
        ssoOnlyHint:
          'People at {{domain}} must use single sign-on. Passwords, passkeys, sign-ups and password resets stop working for them.',
        ssoOnlyNeedsOn: 'Turn the connection on first.',
        ssoOnlySaved: 'Single sign-on is now required for {{domain}}.',
        ssoOnlyOff: 'Passwords and passkeys work again for {{domain}}.',
        jit: 'New people who sign in with single sign-on',
        jitHint:
          'Choose a role to add them automatically, or leave it off to allow invited people only. Roles needing two-factor authentication can’t be chosen.',
        jitOff: 'Invited people only',
        jitSave: 'Save',
        jitSaved: 'New people now join as {{role}}.',
        jitCleared: 'Only invited people can join now.',
        removeConnection: 'Remove connection',
        removeConnectionTitle: 'Remove single sign-on for {{domain}}?',
        removeConnectionBody:
          'People at {{domain}} can’t sign in with single sign-on any more. They can reset a password if they had one.',
        removed: 'Single sign-on for {{domain}} was removed.',
      },
      support: {
        title: 'Support access',
        description:
          'Hamnivo support can read part of this workspace only after you approve it, for a limited time. Access is read-only and every read is recorded in your audit log.',
        loading: 'Loading support access',
        forbidden: 'Support access is approved by workspace owners.',
        pending: 'Waiting for your decision',
        active: 'Active now',
        history: 'Earlier requests',
        none: 'No requests from Hamnivo support.',
        from: '{{name}}, Hamnivo support',
        asked: 'Asked {{time}}',
        reason: 'Reason: {{reason}}',
        areas: 'What they may read',
        minutes: 'For how long',
        duration: '{{minutes}} minutes',
        note: 'Note for support',
        approve: 'Approve',
        decline: 'Decline',
        approved: 'Support access approved until {{time}}.',
        declined: 'The request was declined.',
        revoke: 'Revoke now',
        revokeTitle: 'Revoke support access now?',
        revokeBody: 'Their next read is refused. They can ask again if they still need access.',
        revoked: 'Support access was revoked.',
        endsAt: 'Ends {{time}}',
        pickArea: 'Choose at least one area.',
        states: {
          pending: 'Waiting',
          active: 'Active',
          declined: 'Declined',
          revoked: 'Revoked',
          ended: 'Ended',
          expired: 'Expired',
        },
        scopes: {
          configuration: 'Configuration (members, roles, brands, agents)',
          conversations: 'Conversation list (status only, never messages)',
          audit: 'Audit log',
        },
      },
    },
    notices: {
      label: 'Workspace notices',
      supportActive: '{{name}} from Hamnivo support can read {{areas}} until {{time}}.',
      supportRecorded: 'Every read is recorded in your audit log.',
      review: 'Review',
      supportPending: 'Hamnivo support asked for temporary access. Requests waiting: {{n}}.',
      reviewRequest: 'Review the request',
      incident: {
        notice: 'Notice',
        degraded: 'Service degraded',
        outage: 'Service outage',
      },
      since: 'Since {{time}}',
      channelDown:
        'The {{channel}} channel of {{brand}} has not worked since {{time}}. New messages may not reach your team.',
      channelsDown:
        'Channels that have stopped working: {{n}}. New messages may not reach your team.',
      channelItem: 'The {{channel}} channel of {{brand}}, since {{time}}',
      fixChannel: 'Fix it',
    },
    invitation: {
      title: 'Join a workspace',
      description: 'You were invited to a Hamnivo workspace.',
      signInFirst: 'Sign in with the address the invitation was sent to, then accept it.',
      signIn: 'Sign in to accept',
      createAccount: 'Create an account',
      createHint:
        'New to Hamnivo? Create an account with the invited address, confirm it, then open the invitation link again.',
      signedInAs: 'Signed in as {{email}}',
      accept: 'Accept invitation',
      accepted: 'You joined {{name}}.',
      missing: 'This link has no invitation in it. Open the link from your email again.',
      invalid:
        'This invitation is no longer valid. It may have expired, been used, or been sent to another address. Ask for a new one.',
      unverified: 'Confirm your email address first. We sent you a link when you signed up.',
      otherAccount: 'Sign in with another account',
    },
  },
  sso: {
    continue: 'Continue with single sign-on',
    short: 'Continue with SSO',
    email: 'Work email address',
    emailHint: 'We send you to your organization’s sign-in page.',
    submit: 'Continue',
    back: 'Back to password sign-in',
    notSetUp:
      'Single sign-on isn’t set up for this address. Sign in with your password, or ask your administrator.',
    required: 'Your organization requires single sign-on for this address.',
    popupBlocked:
      'Your browser blocked the sign-in window. Allow pop-ups for Studio, then try again.',
    failed: 'Single sign-on didn’t finish. Try again.',
    errors: {
      sso_email_unverified: 'Your identity provider hasn’t verified your email address.',
      sso_domain_mismatch: 'This identity provider can only sign in addresses in its own domain.',
      sso_unverified_local_account:
        'A Hamnivo account with this address exists but was never confirmed. Confirm it first, then use single sign-on.',
      sso_unavailable: 'Single sign-on isn’t available for this address right now.',
      access_denied: 'Sign-in was cancelled at your identity provider.',
      generic: 'Single sign-on didn’t work. Try again, or contact your administrator.',
    },
    done: 'Signed in. You can close this window.',
  },
  reauthMore: {
    otherWays: 'Or confirm another way',
    passkey: 'Use a passkey',
    sso: 'Use single sign-on',
    ssoDescription:
      'This action needs a recent sign-in. Continue with your organization’s single sign-on. Your work on this page is kept.',
  },
  platform: {
    title: 'Hamnivo Platform',
    notOperator: 'This console is for Hamnivo platform operators.',
    notOperatorHint:
      'Your account isn’t a platform operator. Workspace roles don’t give access here.',
    returnToWorkspace: 'Return to workspace',
    nav: {
      label: 'Platform',
      tenants: 'Tenants',
      health: 'Service health',
      support: 'Support access',
      incidents: 'Incidents',
      audit: 'Audit log',
    },
    roles: {
      platform_admin: 'Platform admin',
      support_operator: 'Support operator',
      platform_viewer: 'Platform viewer',
    },
    mfaNeeded:
      'Turn on two-factor authentication in Account security to ask for or use support access.',
    overview: {
      tenants: 'Tenants',
      active: 'active: {{n}}',
      supportActive: 'Active support access',
      supportPending: 'Waiting for a decision',
      aiStops: 'AI stops',
      providersOff: 'Providers switched off',
    },
    tenants: {
      search: 'Search tenants',
      searchPlaceholder: 'Name or handle',
      loading: 'Loading tenants',
      empty: 'No tenants match.',
      columns: {
        name: 'Tenant',
        status: 'State',
        region: 'Region',
        aiStops: 'AI stops',
        support: 'Support access',
      },
      open: 'Open {{name}}',
      status: { active: 'Active', suspended: 'Suspended', closing: 'Closing' },
      detail: 'Tenant details',
      id: 'Tenant ID',
      created: 'Created',
      region: 'Region',
      locale: 'Default language',
      counts: '{{workspaces}} workspace areas · {{brands}} brands',
      connectors: 'Connectors',
      connectorHealth: 'Last health check {{time}}',
      noConnectors: 'No connectors.',
      aiStops: 'AI stops',
      noAiStops: 'The AI isn’t stopped anywhere.',
      aiStopScope: { tenant: 'Whole tenant', brand: 'One brand', agent: 'One agent' },
      providers: 'Model providers',
      noProviders: 'No provider connections.',
      providerStatus: { active: 'On', disabled: 'Switched off' },
      contentNote: 'Platform operators see metadata only. Customer content needs support access.',
      noAccess: 'No access to customer content',
      noAccessHint:
        'The workspace must approve a request first. Access is read-only and time-limited.',
      requestAccess: 'Request temporary access',
      grants: 'Support access for this tenant',
    },
    request: {
      title: 'Request temporary access to {{name}}',
      description:
        'The workspace owners get an email and decide. Nothing is shared until they approve.',
      reason: 'Reason',
      reasonHint: 'Say what you need to check and why. The workspace sees this.',
      reasonShort: 'Write at least 10 characters.',
      areas: 'What you need to read',
      minutes: 'How long',
      submit: 'Send request',
      sent: 'Request sent. It appears under Support access when the workspace decides.',
    },
    support: {
      intro: 'Your requests for temporary, read-only access to tenant data.',
      loading: 'Loading support access',
      none: 'You haven’t asked for access yet.',
      expiresIn: 'Access expires in {{minutes}} min',
      endsAt: 'Ends {{time}}',
      end: 'End session',
      ended: 'Support access ended.',
      approvedBy: 'Approved by {{name}}',
      declinedBy: 'Declined by {{name}}',
      note: 'Note: {{note}}',
      requestAgain: 'Request again',
      view: {
        configuration: 'View configuration',
        conversations: 'View conversation list',
        audit: 'View audit log',
      },
      reading: 'Loading {{area}}',
      members: 'Members',
      roles: 'Roles',
      brands: 'Brands',
      agents: 'AI agents',
      live: 'Live',
      notLive: 'Not live',
      conversationsNote: 'Status only. Messages are never shared with support.',
    },
    health: {
      checked: 'Checked {{time}}',
      loading: 'Checking services',
      services: {
        api: 'API',
        database: 'Database',
        valkey: 'Cache and queues',
        queue: 'Background delivery',
        connectors: 'Connectors',
      },
      states: { healthy: 'Healthy', degraded: 'Degraded', down: 'Down' },
      queue: 'Waiting events: {{pending}} · failing: {{failing}}',
      oldest: 'Oldest waiting: {{seconds}} s',
      connectors: '{{total}} connectors · {{degraded}} need attention · {{disabled}} off',
      providers: 'Platform model keys',
      configured: 'Set',
      notConfigured: 'Not set',
    },
    incidents: {
      intro: 'Incidents appear as a banner in every workspace until resolved.',
      declare: 'Declare incident',
      loading: 'Loading incidents',
      none: 'No incidents.',
      active: 'Active',
      resolved: 'Resolved {{time}}',
      started: 'Started {{time}}',
      resolve: 'Resolve',
      resolvedDone: 'The incident was resolved. The banner is gone.',
      severity: 'Severity',
      severities: { notice: 'Notice', degraded: 'Degraded', outage: 'Outage' },
      titleField: 'Title',
      message: 'Message for workspaces',
      messageHint: 'Say what is affected and what still works.',
      tooShort: 'Write at least 3 characters.',
      submit: 'Post incident',
      posted: 'The incident banner is showing in every workspace.',
    },
    audit: {
      intro: 'Everything done in this console, and every workspace decision about support access.',
      loading: 'Loading the platform audit log',
      empty: 'Nothing recorded yet.',
      operator: 'Operator',
      workspaceDecision: 'Workspace decision',
      tenant: 'Tenant',
      loadMore: 'Load older entries',
    },
  },
  permissionAreas: {
    agency: 'Agency',
    branding: 'Branding',
    conversations: 'Conversations',
    visitors: 'Visitors',
    inbox: 'Inboxes and routing',
    teams: 'Teams',
    members: 'Members',
    roles: 'Roles',
    grants: 'Access',
    audit: 'Audit log',
    brands: 'Brands',
    widget: 'Chat widget',
    agents: 'AI agents',
    automations: 'Automations',
    knowledge: 'Knowledge',
    tools: 'Tools and actions',
    decisions: 'Decisions',
    memory: 'Customer memory',
    commerce: 'Orders and commerce',
    analytics: 'Reports',
    billing: 'Billing',
    api_keys: 'API keys',
    webhooks: 'Webhooks',
    network: 'Allowed hosts',
    mcp: 'AI assistants (MCP)',
    integrations: 'Integrations',
    secrets: 'Credentials',
    extensions: 'Extensions',
    migration: 'Imports',
    settings: 'Workspace settings',
    security: 'Security',
    privacy: 'Privacy',
    customers: 'Customer data',
    support: 'Support access',
  },
  permissions: {
    agency: {
      read: 'View agency settings and the client directory',
      manage: 'Manage agency settings and independent client links',
    },
    branding: {
      read: 'View portal branding and custom domains',
      manage: 'Manage portal branding and verified custom domains',
    },
    conversations: {
      read: 'Read conversations and customer history',
      reply: 'Reply to customers',
      note: 'Write internal notes',
      assign: 'Assign and transfer conversations',
      takeover: 'Take over a conversation from the AI or another person',
      status: 'Resolve, reopen and snooze conversations',
      tag: 'Tag conversations and fill in their custom fields',
      bulk: 'Change many conversations at once',
      review: 'Review and coach other people’s conversations',
      export: 'Export conversation transcripts',
    },
    visitors: { block: 'Block and unblock visitors who misuse the chat' },
    inbox: { manage: 'Configure inboxes, routing and business hours' },
    teams: { manage: 'Create teams and manage membership' },
    members: {
      read: 'See team members and their roles',
      invite: 'Invite members',
      remove: 'Suspend or remove members',
    },
    roles: { manage: 'Create and edit custom roles' },
    grants: { manage: 'Grant and revoke roles within your own scope' },
    audit: { read: 'Read the audit log', export: 'Export the audit log' },
    brands: { manage: 'Create brands and set allowed websites' },
    widget: {
      configure: 'Configure the chat widget',
      identity_keys: 'Register the keys that verify customer identity',
    },
    agents: {
      read: 'View AI agents and their versions',
      manage: 'Edit AI agent drafts',
      publish: 'Publish or roll back AI agents',
    },
    automations: {
      read: 'View automations and what they did',
      manage: 'Create, publish and turn off automations, including website prompts',
    },
    knowledge: { read: 'Read knowledge sources', manage: 'Add, edit and retire knowledge' },
    tools: {
      read: 'See tools, action policies, AI actions and simulation results',
      manage: 'Configure tools and action policies',
      approve: 'Approve actions proposed by AI agents',
    },
    decisions: {
      read: 'Review routing and quality decisions',
      manage: 'Edit decision rubrics and thresholds',
    },
    memory: {
      read: 'Read customer memory records',
      manage: 'Correct or restrict memory records',
      delete: 'Delete memory records',
      erase: 'Erase everything remembered about a customer',
      export: 'Export everything remembered about a customer',
    },
    commerce: {
      read: 'See carts, quotes, orders, payments and refunds',
      carts: { manage: 'Share products and prepare quotes in conversations' },
      orders: { manage: 'Record fulfilment updates on orders' },
      refunds: { request: 'Ask for a refund for a customer', approve: 'Approve or reject refunds' },
      exceptions: {
        resolve: 'Resolve payment and refund exceptions after checking with the provider',
      },
      merchants: { manage: 'Connect merchants and set their credentials and refund rules' },
    },
    analytics: { read: 'View analytics and reports' },
    billing: {
      read: 'See the plan, usage and invoices',
      manage: 'Manage plan, invoices and payment',
    },
    api_keys: {
      read: 'See API keys and when they were last used',
      manage: 'Create and revoke API keys',
    },
    webhooks: {
      read: 'See webhooks and their delivery log',
      manage: 'Configure outgoing webhooks',
    },
    network: { manage: 'Choose which outside hosts Hamnivo may call' },
    mcp: {
      read: 'Let AI assistants such as Claude read conversations, knowledge and customer memory through Hamnivo’s MCP server',
    },
    integrations: { manage: 'Connect channels and third-party services' },
    secrets: { manage: 'Store and rotate credentials' },
    extensions: { manage: 'Install and remove extensions' },
    migration: {
      review: 'See imports, their reports and sample conversations',
      run: 'Upload exports and run imports',
      manage: 'Approve an import or roll it back',
    },
    settings: { manage: 'Edit workspace settings' },
    security: { manage: 'Configure sign-in, single sign-on and session rules' },
    privacy: { manage: 'Choose how long conversations are kept before they are deleted' },
    customers: {
      export: 'Download everything about one customer, for an access request',
      erase: 'Erase one customer everywhere, Chatwoot included, for an erasure request',
    },
    support: { grant: 'Allow Hamnivo support to access this workspace' },
  },
} as const;

type Strings<T> = { [K in keyof T]: T[K] extends string ? string : Strings<T[K]> };
/** Every locale has exactly the English keys; TypeScript reports a missing one. */
export type Translation = Strings<typeof en>;
